/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { getHintUtils } from '@epic-web/client-hints'
import {
	clientHint as colorSchemeHint,
	subscribeToSchemeChange,
} from '@epic-web/client-hints/color-scheme'
import { clientHint as timeZoneHint } from '@epic-web/client-hints/time-zone'
import { useRevalidator } from '@remix-run/react'
import * as React from 'react'
import { useRequestInfo } from './request-info.ts'

const hintsUtils = getHintUtils({
	theme: colorSchemeHint,
	timeZone: timeZoneHint,
	// add other hints here
})

export const { getHints } = hintsUtils

/**
 * @returns an object with the client hints and their values
 */
export function useHints() {
	const requestInfo = useRequestInfo()
	return requestInfo.hints
}

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export function ClientHintCheck({ nonce }: { nonce: string }) {
	const { revalidate } = useRevalidator()
	React.useEffect(
		() => subscribeToSchemeChange(() => revalidate()),
		[revalidate],
	)

	// Strip the `window.location.reload()` from the generated script:
	// it triggers infinite reload loops in some browser/env combos when a
	// hint's resolved value is unstable across requests. Cookies still get
	// set, so the next navigation will see the right hints.
	const script = hintsUtils
		.getClientHintCheckScript()
		.replace(
			'if (cookieChanged) window.location.reload();',
			'// reload disabled to avoid infinite loops; cookies are still set above',
		)

	return (
		<script
			nonce={nonce}
			dangerouslySetInnerHTML={{ __html: script }}
		/>
	)
}
